import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Box,
  Typography,
  Chip,
  Link,
  TableSortLabel,
} from '@mui/material';
import { ExpandMore, ExpandLess, VerifiedUser } from '@mui/icons-material';
import useSortTable from '../../../hooks/useSortTable';

const stripHtml = (html) => html?.replace(/<[^>]*>/g, '') || '';

const ThreadTable = ({ threads, readOnly }) => {
  const [expanded, setExpanded] = useState({});
  const { sorted, sortBy, sortDir, handleSort } = useSortTable(threads);

  const toggleExpanded = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const SortCell = ({ label, field }) => (
    <TableCell onClick={() => handleSort(field)} sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      <TableSortLabel active={sortBy === field} direction={sortBy === field ? sortDir : 'asc'}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Paper>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortCell label="Usuario" field="users_correspondence_threads_from_idTousers.name" />
              <TableCell>Mensaje</TableCell>
              <TableCell>Etiquetados</TableCell>
              <SortCell label="Fecha" field="createdAt" />
              <TableCell>Firma</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No hay hilos de conversacion. Haz clic en "Crear Hilo" para agregar uno.
                </TableCell>
              </TableRow>
            ) : (
              sorted.map((thread) => {
                const fromUser = thread.users_correspondence_threads_from_idTousers;
                const taggedUsers = Array.isArray(thread.tagged_users_data)
                  ? thread.tagged_users_data
                  : [];

                return (
                  <TableRow key={thread.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {fromUser?.name || thread.user?.name || 'Usuario'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'rgba(29,31,32,0.55)' }}>
                        {fromUser?.email || thread.user?.email || ''}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 400 }}>
                      {expanded[thread.id] ? (
                        <Box>
                          <Box
                            sx={{ maxWidth: 500, '& *': { maxWidth: '100%' }, fontSize: '0.875rem' }}
                            dangerouslySetInnerHTML={{ __html: thread.message }}
                          />
                          <Link
                            component="button"
                            variant="caption"
                            onClick={() => toggleExpanded(thread.id)}
                            sx={{ display: 'flex', alignItems: 'center', mt: 0.5, color: '#5980a6' }}
                          >
                            <ExpandLess fontSize="small" /> Ver menos
                          </Link>
                        </Box>
                      ) : (
                        <Box>
                          <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                            {stripHtml(thread.message)}
                          </Typography>
                          {thread.message && (
                            <Link
                              component="button"
                              variant="caption"
                              onClick={() => toggleExpanded(thread.id)}
                              sx={{ display: 'flex', alignItems: 'center', color: '#5980a6' }}
                            >
                              <ExpandMore fontSize="small" /> Ver mensaje
                            </Link>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {taggedUsers.length > 0 ? (
                        <Typography variant="body2">
                          {taggedUsers.map(u => u.name).join(', ')}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">&mdash;</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2">
                        {formatDate(thread.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {thread.electronicSignature ? (
                        <Tooltip
                          title={`Firmado electronicamente — Token: ${thread.electronicSignature.signatureToken} | Firmante: ${thread.electronicSignature.signerName} | Decreto 2364/2012`}
                          arrow
                        >
                          <Chip
                            icon={<VerifiedUser fontSize="small" />}
                            label="Firmado"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        </Tooltip>
                      ) : (
                        <Chip label="Sin firma" size="small" variant="outlined" sx={{ borderColor: 'rgba(29,31,32,0.16)' }} />
                      )}
                    </TableCell>
                    <TableCell>
                      {thread.electronicSignature?.documentUrl && (
                        <Tooltip title="Descargar">
                          <a href={thread.electronicSignature.documentUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#5980a6' }}>
                            ↓
                          </a>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ThreadTable;
